import { action } from '@ember/object';
import { htmlSafe } from '@ember/template';
import type { SafeString } from '@ember/template/-private/handlebars';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

type Position = 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
type Status = 'started' | 'complete' | 'failed';

const EXPORT_STATUS = {
  STARTED: 'started' as const,
  COMPLETE: 'complete' as const,
  FAILED: 'failed' as const,
};

interface ExportStatusArgs {
  position?: Position;
  status: Status;
  title: string;
  message: string;
  linkText?: string;
  linkHref?: string;
  onClick?: () => void;
}

export default class ExportStatus extends Component<ExportStatusArgs> {
  @tracked isMinimized = false;

  get position(): Position {
    return this.args.position || 'top-right';
  }

  get statusIcon(): string {
    switch (this.args.status) {
      case EXPORT_STATUS.STARTED:
        return 'loading-spinner';
      case EXPORT_STATUS.COMPLETE:
        return 'circle-check';
      case EXPORT_STATUS.FAILED:
        return 'circle-x';
      default:
        return '';
    }
  }

  get statusColorClass(): string {
    switch (this.args.status) {
      case EXPORT_STATUS.STARTED:
        return 'status-started';
      case EXPORT_STATUS.COMPLETE:
        return 'status-complete';
      case EXPORT_STATUS.FAILED:
        return 'status-failed';
      default:
        return '';
    }
  }

  get style(): SafeString {
    const styles = this.calculatePosition();
    return htmlSafe(styles);
  }

  calculatePosition(): string {
    const baseStyles = 'position: fixed; z-index: 100000000;';
    const offset = '24px';

    switch (this.position) {
      case 'top-right':
        return `${baseStyles} top: ${offset}; right: ${offset};`;
      case 'top-left':
        return `${baseStyles} top: ${offset}; left: ${offset};`;
      case 'bottom-right':
        return `${baseStyles} bottom: ${offset}; right: ${offset};`;
      case 'bottom-left':
        return `${baseStyles} bottom: ${offset}; left: ${offset};`;
      default:
        return `${baseStyles} top: ${offset}; right: ${offset};`;
    }
  }

  @action
  toggleMinimize(): void {
    this.isMinimized = !this.isMinimized;
  }

  @action
  close(): void {
    this.args.onClick?.();
  }
}
